/**
 * 기기 신뢰 — '내 기기' API (server/routes/devices.js의 /this · /mine)
 *
 * 판정은 서버다. 여기는 서명을 붙여 묻고 결과를 돌려줄 뿐이다.
 */
import { apiFetch } from './apiClient';
import { buildDeviceBlock, DeviceStatusInfo, DeviceTrustMode } from './deviceIdentity';
import type { User } from '../types';

export interface MyDevice {
  id: number;
  kind: 'desktop' | 'web';
  status: 'pending' | 'approved' | 'rejected' | 'revoked' | 'expired';
  fingerprint: string;
  label: string;
  computerName: string;
  platform: Record<string, unknown>;
  selfConfirmed: boolean;
  requestedAt: string;
  decidedAt: string | null;
  decidedByName: string | null;
  decisionReason: string | null;
  autoReason: 'internal' | 'migration' | null;
  lastSeenAt: string | null;
  current: boolean;
}

export interface ThisDeviceResult {
  mode: DeviceTrustMode;
  /** 이 기기 상태(서버가 아는 경우). 대기 중이면 확인 코드 포함 */
  device: DeviceStatusInfo | null;
  approved?: boolean;
  /** 승인 요청을 보낼 수 있는 웹 기기인가 */
  canRequest?: boolean;
  /** 승인된 기기인데 지금 토큰에 기기가 안 묶였다 — 서명을 실어 refresh하면 묶인다 */
  needsRefresh?: boolean;
  /** 키를 만들 수 없는 기기(개인정보 보호 모드 등) */
  unsupported?: boolean;
}

/**
 * 이 기기 상태 확인 + (PC는) 자동 등록 + (웹은 requestApproval일 때) 승인 요청.
 * observe 단계에서 이미 로그인한 세션이 기기를 등록하는 경로다.
 */
export async function checkThisDevice(
  user: Pick<User, 'affiliateId' | 'userid'>,
  opts: { requestApproval?: boolean; label?: string } = {}
): Promise<ThisDeviceResult> {
  if (!user.affiliateId || !user.userid) return { mode: 'off', device: null };
  const built = await buildDeviceBlock('register', user.affiliateId, user.userid, opts);
  if (!built) return { mode: 'off', device: null, unsupported: true };
  if (built.mode === 'off' || !built.block) return { mode: 'off', device: null };
  return apiFetch<ThisDeviceResult>('/devices/this', {
    method: 'POST',
    body: JSON.stringify({ device: built.block }),
  });
}

export function getMyDevices(): Promise<{ mode: DeviceTrustMode; currentDeviceId: number | null; canConfirm: boolean; devices: MyDevice[] }> {
  return apiFetch('/devices/mine');
}

export function revokeMyDevice(id: number): Promise<{ ok: boolean; wasCurrent: boolean }> {
  return apiFetch(`/devices/mine/${id}`, { method: 'DELETE' });
}

export function confirmMyDevice(id: number, code: string): Promise<{ ok: boolean }> {
  return apiFetch(`/devices/mine/${id}/confirm`, { method: 'POST', body: JSON.stringify({ code }) });
}

export function reportMyDevice(id: number): Promise<{ ok: boolean }> {
  return apiFetch(`/devices/mine/${id}/report`, { method: 'POST' });
}
